import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Share2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";

const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function HotTakes() {
  const { user } = useAuth();
  const [takes, setTakes] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  const load = async () => {
    try { setTakes((await api.get("/hot-takes")).data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!user) return login();
    if (!text.trim()) return toast.error("Say something first");
    setPosting(true);
    try {
      const t = (await api.post("/hot-takes", { text })).data;
      setTakes((prev) => [t, ...prev]); setText("");
      toast.success("Take posted 🔥");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const vote = async (take, v) => {
    if (!user) return login();
    if (take.my_vote) return;
    try {
      const r = (await api.post(`/hot-takes/${take.id}/vote`, { vote: v })).data;
      setTakes((prev) => prev.map((t) => t.id === take.id ? r : t));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const shareTake = (t) => share({ kind: "hottake", text: t.text, agree_pct: t.agree_pct }, t.id);

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2"><Flame className="text-brand" size={24} /><span className="font-heading font-bold text-xl tracking-tighter">RoastLit</span></a>
        {user ? <a href="/app/submit" className="text-sm text-zinc-300 hover:text-brand">Open app</a>
          : <button data-testid="hot-login-btn" onClick={login} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10">Sign in</button>}
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-6 py-10" data-testid="hot-takes">
        <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Hot Takes</h1>
        <p className="text-zinc-400 mt-2 mb-6">Drop an opinion. See how many strangers agree. No account needed to look.</p>

        <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4 mb-8">
          <textarea data-testid="hottake-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={200}
            placeholder="e.g. Pineapple belongs on pizza."
            className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-zinc-600">{text.length}/200 · standard content rules apply</span>
            <button data-testid="post-hottake-btn" onClick={post} disabled={posting}
              className="px-5 py-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm transition-colors disabled:opacity-50">
              {posting ? "Posting…" : "Post take"}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {takes.length === 0 && <div className="text-center text-zinc-500 py-10">No takes yet. Be the first 🔥</div>}
          {takes.map((t) => {
            const voted = !!t.my_vote;
            const majority = t.agree_pct >= 50;
            return (
              <div key={t.id} data-testid={`take-${t.id}`} className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
                <p className="text-lg text-zinc-50 leading-snug">"{t.text}"</p>
                <div className="flex gap-2 mt-4">
                  <button data-testid={`vote-agree-${t.id}`} onClick={() => vote(t, "agree")} disabled={voted}
                    className={`flex-1 py-2.5 rounded-full text-sm font-bold border transition-colors ${t.my_vote === "agree" ? "border-tier-mild bg-tier-mild/20 text-white" : "border-white/10 text-zinc-300 hover:border-white/25 disabled:opacity-60"}`}>
                    🔥 Agree {voted ? `· ${t.agree}` : ""}
                  </button>
                  <button data-testid={`vote-disagree-${t.id}`} onClick={() => vote(t, "disagree")} disabled={voted}
                    className={`flex-1 py-2.5 rounded-full text-sm font-bold border transition-colors ${t.my_vote === "disagree" ? "border-brand bg-brand/20 text-white" : "border-white/10 text-zinc-300 hover:border-white/25 disabled:opacity-60"}`}>
                    💀 Disagree {voted ? `· ${t.disagree}` : ""}
                  </button>
                </div>
                {voted && (
                  <div className="mt-3 flex items-center justify-between" data-testid={`result-${t.id}`}>
                    <span className="text-sm text-zinc-300"><b style={{ color: majority ? "#10b981" : "#ef4444" }}>{t.agree_pct}% agree</b> · you're in the {majority ? "majority" : "minority"}</span>
                    <button data-testid={`share-take-${t.id}`} onClick={() => shareTake(t)} disabled={busyId === t.id}
                      className="inline-flex items-center gap-1.5 text-sm text-brand font-bold hover:brightness-110 disabled:opacity-50">
                      <Share2 size={14} /> {busyId === t.id ? "…" : "Share"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-xl border border-brand/30 bg-brand/10 p-5 flex items-center justify-between gap-3">
          <div className="font-bold text-zinc-100">Enjoyed that? Get roasted for real.</div>
          <button data-testid="hottake-cta" onClick={() => (user ? (window.location.href = "/app/submit") : login())}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm">
            Get roasted <ArrowRight size={15} />
          </button>
        </div>
      </main>
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
